import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlarmClock, CalendarClock, Check, Infinity as InfinityIcon, Phone, RefreshCw, Search, Store, UserRound, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import Pagination, { paginate } from '../../components/Pagination';
import API from '../../utils/api';
import './PlanRenewals.css';

const PAGE_SIZE = 10;
const SOON_DAYS = 5; // same window the members are warned in

const PRESETS = [
    { value: 1, unit: 'months', label: '+1 month' },
    { value: 3, unit: 'months', label: '+3 months' },
    { value: 6, unit: 'months', label: '+6 months' },
    { value: 1, unit: 'years', label: '+1 year' },
    { value: 2, unit: 'years', label: '+2 years' },
];

const daysLeft = sub => Math.ceil((new Date(sub.expiresAt) - new Date()) / 86400000);

// "running" | "soon" | "expired" | "cancelled" | "lifetime"
const stateOf = sub => {
    if (sub.cancelledAt) return 'cancelled';
    if (sub.noExpiry) return 'lifetime';
    const days = daysLeft(sub);
    if (days <= 0) return 'expired';
    return days <= SOON_DAYS ? 'soon' : 'running';
};

const STATE_TEXT = { running: 'Active', soon: 'Expiring soon', expired: 'Expired', cancelled: 'Cancelled', lifetime: 'Lifetime' };

// same date maths as the server: months/years keep the day of the month, clamped to the month's end
const addDuration = (start, value, unit) => {
    const date = new Date(start);
    if (unit === 'days') { date.setDate(date.getDate() + value); return date; }
    if (unit === 'years') {
        const month = date.getMonth();
        date.setFullYear(date.getFullYear() + value);
        if (date.getMonth() !== month) date.setDate(0);
        return date;
    }
    const day = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + value);
    date.setDate(Math.min(day, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
    return date;
};

const fmt = date => new Date(date).toLocaleDateString('en-IN');

const PlanRenewals = () => {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [audience, setAudience] = useState('member');
    const [filter, setFilter] = useState('soon');          // soon | expired | running | all
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [target, setTarget] = useState(null);            // subscription being extended
    const [amount, setAmount] = useState({ value: 1, unit: 'years' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await API.get('/subscriptions/admin/all', { params: { limit: 2000 } });
            setSubs(data.subscriptions || []);
        } catch {
            toast.error('Could not load subscriptions');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // only the newest subscription of each account matters here; older ones are history
    const current = useMemo(() => {
        const seen = new Set();
        return [...subs]
            .sort((a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt))
            .filter(sub => {
                const id = String(sub.user?._id || sub.user);
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
            });
    }, [subs]);

    const ofAudience = useMemo(() => current.filter(sub => sub.targetType === audience), [current, audience]);
    const counts = useMemo(() => ({
        soon: ofAudience.filter(sub => stateOf(sub) === 'soon').length,
        expired: ofAudience.filter(sub => stateOf(sub) === 'expired').length,
        running: ofAudience.filter(sub => ['running', 'soon'].includes(stateOf(sub))).length,
        all: ofAudience.length,
    }), [ofAudience]);
    const audienceTotals = useMemo(() => ({
        member: current.filter(sub => sub.targetType === 'member' && stateOf(sub) === 'soon').length,
        service: current.filter(sub => sub.targetType === 'service' && stateOf(sub) === 'soon').length,
    }), [current]);

    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return ofAudience
            .filter(sub => {
                // typing a name or number searches every plan of this audience, whichever filter is selected
                if (!query) {
                    const state = stateOf(sub);
                    if (filter === 'soon' && state !== 'soon') return false;
                    if (filter === 'expired' && state !== 'expired') return false;
                    if (filter === 'running' && !['running', 'soon'].includes(state)) return false;
                    return true;
                }
                return true && [sub.user?.name, sub.user?.businessName, sub.user?.mobile, sub.planNameSnapshot]
                    .some(value => String(value || '').toLowerCase().includes(query));
            })
            .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    }, [ofAudience, filter, search]);

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const openExtend = sub => { setTarget(sub); setAmount({ value: 1, unit: 'years' }); };
    const newEnd = useMemo(() => {
        if (!target || !Number.isInteger(Number(amount.value)) || Number(amount.value) < 1) return null;
        const from = new Date(target.expiresAt) > new Date() ? target.expiresAt : new Date();
        return addDuration(from, Number(amount.value), amount.unit);
    }, [target, amount]);

    const extend = async () => {
        if (!target || !newEnd) return;
        setSaving(true);
        try {
            const { data } = await API.post(`/subscriptions/admin/${target._id}/extend`, { value: Number(amount.value), unit: amount.unit });
            toast.success(data.message || 'Plan extended');
            setTarget(null);
            load(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not extend the plan');
        } finally {
            setSaving(false);
        }
    };

    const FILTERS = [
        { id: 'soon', label: `Expiring in ${SOON_DAYS} days`, count: counts.soon },
        { id: 'expired', label: 'Expired', count: counts.expired },
        { id: 'running', label: 'Running', count: counts.running },
        { id: 'all', label: 'All', count: counts.all },
    ];

    return (
        <div className="renew-layout">
            <Sidebar />
            <main className="admin-content renew-content">
                <Navbar />
                <div className="renew-page">
                    <header className="renew-heading">
                        <div>
                            <div className="renew-eyebrow"><CalendarClock size={14} /> Subscriptions</div>
                            <h1>Plan renewals</h1>
                            <p>See whose plan is about to end and extend members and service providers. Accounts get an “expires soon” alert {SOON_DAYS} days before, and drop to Free when a plan runs out.</p>
                        </div>
                        <button type="button" className="renew-refresh" onClick={() => load()}><RefreshCw size={16} /> Refresh</button>
                    </header>

                    <div className="renew-tabs" role="tablist" aria-label="Audience">
                        {[{ id: 'member', label: 'Members', Icon: UserRound }, { id: 'service', label: 'Service providers', Icon: Store }].map(({ id, label, Icon }) => (
                            <button key={id} type="button" role="tab" aria-selected={audience === id} className={audience === id ? 'is-active' : ''} onClick={() => { setAudience(id); setPage(1); }}>
                                <Icon size={15} />{label}
                                {audienceTotals[id] > 0 && <b title={`${audienceTotals[id]} expiring soon`}>{audienceTotals[id]}</b>}
                            </button>
                        ))}
                    </div>

                    <section className="renew-card">
                        <div className="renew-toolbar">
                            <div className="renew-filters">
                                {FILTERS.map(item => (
                                    <button key={item.id} type="button" className={filter === item.id && !search.trim() ? 'is-active' : ''} onClick={() => { setFilter(item.id); setPage(1); }}>
                                        {item.label}<b>{item.count}</b>
                                    </button>
                                ))}
                            </div>
                            <label className="renew-search"><Search size={15} /><input placeholder="Search name, mobile or plan…" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} />{search && <button type="button" onClick={() => setSearch('')}><X size={13} /></button>}</label>
                        </div>

                        <div className="renew-table-wrap">
                            <table>
                                <thead><tr><th>{audience === 'member' ? 'Member' : 'Provider'}</th><th>Plan</th><th>Started</th><th>Ends</th><th>Time left</th><th>Status</th><th>Extended</th><th /></tr></thead>
                                <tbody>
                                    {paginate(rows, page, PAGE_SIZE).map(sub => {
                                        const state = stateOf(sub);
                                        const days = daysLeft(sub);
                                        return (
                                            <tr key={sub._id}>
                                                <td><div className="renew-person"><span><UserRound size={15} /></span><div><b>{sub.user?.name || sub.user?.businessName || 'Unknown account'}</b><small><Phone size={11} />{sub.user?.mobile || '—'}</small></div></div></td>
                                                <td><b>{sub.planNameSnapshot}</b></td>
                                                <td>{fmt(sub.purchasedAt)}</td>
                                                <td>{state === 'lifetime' ? 'No expiry' : fmt(sub.expiresAt)}</td>
                                                <td>{state === 'lifetime' ? <InfinityIcon size={15} /> : state === 'cancelled' ? '—' : days > 0 ? `${days} day${days === 1 ? '' : 's'}` : `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`}</td>
                                                <td><span className={`renew-status is-${state}`}>{state === 'soon' ? <AlarmClock size={12} /> : state === 'running' || state === 'lifetime' ? <Check size={12} /> : <X size={12} />}{STATE_TEXT[state]}</span></td>
                                                <td>{sub.extensions?.length ? `${sub.extensions.length}×` : '—'}</td>
                                                <td>{state !== 'lifetime' && state !== 'cancelled' && <button type="button" className="renew-extend" onClick={() => openExtend(sub)}><CalendarClock size={14} /> Extend</button>}</td>
                                            </tr>
                                        );
                                    })}
                                    {!rows.length && (
                                        <tr><td colSpan={8}><div className="renew-empty">{loading ? 'Loading…' : search.trim() ? `No ${audience === 'member' ? 'member' : 'provider'} matches “${search.trim()}”.` : filter === 'soon' ? `No ${audience === 'member' ? 'member' : 'provider'} plans end in the next ${SOON_DAYS} days.` : 'Nothing to show here.'}</div></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} pageSize={PAGE_SIZE} total={rows.length} onChange={setPage} alwaysShow />
                    </section>
                </div>
            </main>

            {target && (
                <div className="renew-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !saving) setTarget(null); }}>
                    <section className="renew-dialog" role="dialog" aria-modal="true" aria-labelledby="renew-title">
                        <header>
                            <div><h3 id="renew-title">Extend plan</h3><p>{target.user?.name || target.user?.businessName} · {target.planNameSnapshot}</p></div>
                            <button type="button" onClick={() => setTarget(null)} aria-label="Close" disabled={saving}><X size={17} /></button>
                        </header>
                        <div className="renew-dialog__now">
                            <div><span>Current end date</span><b>{fmt(target.expiresAt)}</b></div>
                            <div><span>{stateOf(target) === 'expired' ? 'Restarts from' : 'Extends from'}</span><b>{fmt(new Date(target.expiresAt) > new Date() ? target.expiresAt : new Date())}</b></div>
                        </div>
                        <div className="renew-presets">
                            {PRESETS.map(preset => (
                                <button key={preset.label} type="button" className={Number(amount.value) === preset.value && amount.unit === preset.unit ? 'is-active' : ''} onClick={() => setAmount({ value: preset.value, unit: preset.unit })}>{preset.label}</button>
                            ))}
                        </div>
                        <div className="renew-custom">
                            <span>Or choose</span>
                            <input type="number" min="1" value={amount.value} onChange={event => setAmount({ ...amount, value: event.target.value })} />
                            <Select value={amount.unit} allowEmpty={false} onChange={unit => setAmount({ ...amount, unit })} ariaLabel="Unit"
                                options={[{ value: 'days', label: 'Days' }, { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' }]} />
                        </div>
                        <p className="renew-dialog__result">{newEnd ? <>New end date: <b>{fmt(newEnd)}</b></> : 'Enter a whole number of 1 or more.'}</p>
                        <footer>
                            <button type="button" onClick={() => setTarget(null)} disabled={saving}>Cancel</button>
                            <button type="button" className="is-primary" onClick={extend} disabled={saving || !newEnd}>{saving ? 'Extending…' : 'Extend plan'}</button>
                        </footer>
                    </section>
                </div>
            )}
        </div>
    );
};

export default PlanRenewals;
